import { Injectable } from '@angular/core';

declare global {
  interface Window {
    google?: typeof google;
  }
}

/**
 * Loads the Google Maps JavaScript SDK exactly once, however many components ask for it.
 * The API key comes from the backend (config.google.mapsBrowserApiKey) rather than being
 * baked into the frontend build, so it can be rotated without a redeploy.
 *
 * Google's script tag must only ever be inserted once per page load — a second `<script>`
 * pointed at maps/api/js (even with a different `libraries` list) triggers a console
 * warning and undefined behavior for which copy of the SDK actually wins. So the first
 * `load()` call wins the script tag; anything requested afterward (by a different
 * component, possibly with a library the first caller didn't need) is fetched via
 * `google.maps.importLibrary`, which Google explicitly supports once the initial script
 * has finished loading.
 */
@Injectable({ providedIn: 'root' })
export class GoogleMapsLoaderService {
  private loadPromise: Promise<typeof google> | null = null;
  private loadedApiKey: string | null = null;
  private readonly importedLibraries = new Set<string>();

  load(apiKey: string, libraries: string[] = []): Promise<typeof google> {
    if (this.loadPromise) {
      if (this.loadedApiKey !== apiKey) {
        return Promise.reject(new Error('Google Maps was already loaded with a different API key'));
      }
      return this.loadPromise.then(g => this.importMissingLibraries(g, libraries));
    }

    this.loadedApiKey = apiKey;
    const promise = new Promise<typeof google>((resolve, reject) => {
      const callbackName = '__krcGoogleMapsLoaded';
      (window as any)[callbackName] = () => {
        delete (window as any)[callbackName];
        if (window.google) resolve(window.google);
        else reject(new Error('Google Maps script loaded but window.google is missing'));
      };

      const script = document.createElement('script');
      const librariesParam = libraries.length ? `&libraries=${libraries.join(',')}` : '';
      script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&v=weekly&loading=async${librariesParam}&callback=${callbackName}`;
      script.async = true;
      script.onerror = () => reject(new Error('Failed to load the Google Maps script'));
      document.head.appendChild(script);
    }).then(g => {
      libraries.forEach(lib => this.importedLibraries.add(lib));
      return g;
    });
    promise.catch(() => { this.loadPromise = null; this.loadedApiKey = null; });

    this.loadPromise = promise;
    return promise;
  }

  private async importMissingLibraries(g: typeof google, libraries: string[]): Promise<typeof google> {
    const missing = libraries.filter(lib => !this.importedLibraries.has(lib));
    if (missing.length) {
      await Promise.all(missing.map(lib => g.maps.importLibrary(lib)));
      missing.forEach(lib => this.importedLibraries.add(lib));
    }
    return g;
  }
}
